import { useEffect, useState } from "react";

import api from "../api/client";

/**
 * First-run appliance setup wizard.
 *
 * Shown by the SetupGate in App.tsx whenever GET /api/v1/setup/status
 * reports the appliance isn't configured yet. These endpoints are open
 * (unauthenticated) while setup is incomplete — see routers/setup.py.
 *
 * Steps: welcome → admin account → company/branding → outbound email →
 * review → complete. On finish we mark setup complete and bounce to the
 * login screen so the operator signs in with the admin they just made.
 */

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Australia/Sydney",
  "UTC",
];

type AdminForm = { username: string; email: string; password: string; confirm: string };
type CompanyForm = { business_name: string; company_name: string; app_timezone: string };

// Mirrors services/provisioning.slugify_business on the backend: lowercase,
// non-alphanumeric runs -> hyphen, trimmed. Used for the live URL preview.
const slugifyBiz = (s: string) =>
  (s || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
// No outbound account here any more: the appliance signs and delivers its own
// mail, so there is no host, port, username or password to collect. The only
// first-run question left is where alerts should be delivered.
type EmailForm = {
  alert_email: string;
};

type LicenseForm = { hub_url: string; public_key: string; token: string };

const STEPS = ["Welcome", "Admin account", "Company", "Email", "License", "Finish"] as const;

export default function SetupWizard() {
  const [step, setStep] = useState(0);
  const [err, setErr] = useState("");
  const [working, setWorking] = useState(false);
  const [adminCreated, setAdminCreated] = useState(false);

  const [admin, setAdmin] = useState<AdminForm>({ username: "", email: "", password: "", confirm: "" });
  const [company, setCompany] = useState<CompanyForm>({
    business_name: "",
    company_name: "",
    app_timezone: "America/New_York",
  });
  const [email, setEmail] = useState<EmailForm>({ alert_email: "" });
  const [license, setLicense] = useState<LicenseForm>({ hub_url: "", public_key: "", token: "" });
  const [licenseMsg, setLicenseMsg] = useState("");

  // On a re-run from Settings an admin already exists and settings are saved —
  // pre-fill the non-secret fields and mark the admin step satisfied so it's a
  // no-op Next. Secrets (the mailbox password) are never read back; leave blank.
  useEffect(() => {
    api
      .get("/api/v1/setup/status")
      .then((r) => {
        if (r.data?.has_admin) setAdminCreated(true);
      })
      .catch(() => {});
    api
      .get("/api/v1/setup/values")
      .then((r) => {
        const v: Record<string, string> = r.data || {};
        setCompany((c) => ({
          business_name: v.business_name ?? c.business_name,
          company_name: v.company_name ?? c.company_name,
          app_timezone: v.app_timezone ?? c.app_timezone,
        }));
        setEmail((e) => ({ alert_email: v.alert_email ?? e.alert_email }));
        setLicense((l) => ({
          hub_url: v.license_hub_url ?? l.hub_url,
          public_key: v.license_hub_public_key ?? l.public_key,
          token: l.token,
        }));
      })
      .catch(() => {});
  }, []);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const detail = (e: unknown) =>
    (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;

  const createAdmin = async () => {
    setErr("");
    if (admin.password.length < 8) return setErr("Password must be at least 8 characters.");
    if (admin.password !== admin.confirm) return setErr("Passwords don't match.");
    setWorking(true);
    try {
      await api.post("/api/v1/setup/admin", {
        username: admin.username,
        email: admin.email,
        password: admin.password,
      });
      setAdminCreated(true);
      next();
    } catch (e: unknown) {
      const d = detail(e);
      setErr(typeof d === "string" ? d : "Could not create the admin account.");
    } finally {
      setWorking(false);
    }
  };

  const saveCompany = async () => {
    setErr("");
    if (!company.business_name.trim()) return setErr("Business name is required.");
    if (!slugifyBiz(company.business_name))
      return setErr("Business name must contain letters or numbers.");
    if (!company.company_name.trim()) return setErr("Company name is required.");
    setWorking(true);
    try {
      await api.put("/api/v1/setup/settings", {
        settings: {
          business_name: company.business_name,
          company_name: company.company_name,
          // Brand colour and support address are NOT sent. They are part of
          // the product's identity, not the deployment's, and the server
          // refuses them (services/brand_lock.py). The business name is the
          // one piece of branding an appliance sets.
          app_timezone: company.app_timezone,
        },
      });
      next();
    } catch (e: unknown) {
      const d = detail(e);
      setErr(typeof d === "string" ? d : "Could not save company settings.");
    } finally {
      setWorking(false);
    }
  };

  const saveEmail = async () => {
    setErr("");
    setWorking(true);
    try {
      await api.put("/api/v1/setup/settings", {
        settings: { alert_email: email.alert_email },
      });
      next();
    } catch (e: unknown) {
      const d = detail(e);
      setErr(typeof d === "string" ? d : "Could not save email settings.");
    } finally {
      setWorking(false);
    }
  };

  const saveLicense = async () => {
    setLicenseMsg("");
    if (!license.token.trim()) {
      // No token entered — treat License as skipped.
      next();
      return;
    }
    setWorking(true);
    try {
      const r = await api.post("/api/v1/setup/license", {
        hub_url: license.hub_url || undefined,
        public_key: license.public_key || undefined,
        token: license.token.trim(),
      });
      setLicenseMsg(`Activated ${r.data.license_id} (${r.data.tier}).`);
      next();
    } catch (e: unknown) {
      const d = detail(e);
      setLicenseMsg(typeof d === "string" ? d : "Activation failed.");
    } finally {
      setWorking(false);
    }
  };

  const finish = async () => {
    setErr("");
    setWorking(true);
    try {
      await api.post("/api/v1/setup/complete", {});
      // Setup done — go sign in with the new admin account.
      window.location.href = "/login";
    } catch (e: unknown) {
      const d = detail(e);
      setErr(typeof d === "string" ? d : "Could not complete setup.");
      setWorking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-xl bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
        {/* Progress header */}
        <div className="px-6 pt-6">
          <div className="flex items-center justify-between mb-1">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Appliance setup</h1>
            <span className="text-xs text-gray-400">
              Step {step + 1} of {STEPS.length}
            </span>
          </div>
          <div className="flex gap-1 mb-4">
            {STEPS.map((s, i) => (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-brand" : "bg-gray-200 dark:bg-gray-700"}`}
                title={s}
              />
            ))}
          </div>
        </div>

        <div className="px-6 pb-6">
          {err && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-3 py-2 rounded-lg text-sm">
              {err}
            </div>
          )}

          {step === 0 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Welcome. This short wizard configures your RMM appliance — it takes about a
                minute. You'll create the first administrator account, set your company
                details and timezone, and configure outbound email. You can change all of
                this later from Settings.
              </p>
              <div className="flex justify-end">
                <button onClick={next} className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium">
                  Get started
                </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Create the administrator account you'll use to sign in.
              </p>
              <Field label="Username">
                <input
                  value={admin.username}
                  onChange={(e) => setAdmin({ ...admin, username: e.target.value })}
                  disabled={adminCreated}
                  className="w-full border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={admin.email}
                  onChange={(e) => setAdmin({ ...admin, email: e.target.value })}
                  disabled={adminCreated}
                  className="w-full border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Password">
                  <input
                    type="password"
                    value={admin.password}
                    onChange={(e) => setAdmin({ ...admin, password: e.target.value })}
                    disabled={adminCreated}
                    className="w-full border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  />
                </Field>
                <Field label="Confirm">
                  <input
                    type="password"
                    value={admin.confirm}
                    onChange={(e) => setAdmin({ ...admin, confirm: e.target.value })}
                    disabled={adminCreated}
                    className="w-full border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  />
                </Field>
              </div>
              <StepNav onBack={back} onNext={adminCreated ? next : createAdmin}
                nextLabel={adminCreated ? "Next" : "Create admin"} working={working}
                nextDisabled={!adminCreated && (!admin.username || !admin.email || !admin.password)} />
              {adminCreated && (
                <p className="text-xs text-green-600 dark:text-green-400">✓ Admin account created.</p>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">Branding shown across the app and on customer emails / invoices.</p>
              <Field label="Business name">
                <input
                  value={company.business_name}
                  onChange={(e) => setCompany({ ...company, business_name: e.target.value })}
                  placeholder="e.g. Northwind IT"
                  className="w-full border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This sets your AeroGauge RMM address. Your console and all agents will use:
                </p>
                <p className="text-sm font-mono text-brand dark:text-brand-light mt-0.5">
                  https://{slugifyBiz(company.business_name) || "your-business"}.aerogauge.net
                </p>
                {/* The helpdesk mailbox follows the same slug, so it is shown
                    here rather than asked for later — one name drives both the
                    console hostname and the support address. */}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  It also sets your helpdesk address, which customers email to open tickets:
                </p>
                <p className="text-sm font-mono text-brand dark:text-brand-light mt-0.5">
                  helpdesk@{slugifyBiz(company.business_name) || "your-business"}.aerogauge.net
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Finish connecting it under <span className="font-medium">HelpDesk → Mail setup</span>{" "}
                  after setup — that step verifies mail routing and signs in to the mailbox.
                </p>
              </Field>
              <Field label="Company name (display)">
                <input
                  value={company.company_name}
                  onChange={(e) => setCompany({ ...company, company_name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                />
              </Field>
              <div className="grid grid-cols-1 gap-3">
                <Field label="Timezone">
                  <select
                    value={company.app_timezone}
                    onChange={(e) => setCompany({ ...company, app_timezone: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <StepNav onBack={back} onNext={saveCompany} nextLabel="Next" working={working} />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This appliance sends its own email &mdash; there is no mail account to set up and
                nothing runs through Microsoft&nbsp;365 or Google. Addresses are derived from your
                business name, so all that&rsquo;s needed here is where alerts should land.
              </p>
              <Field label="Send alerts to">
                <input
                  type="email"
                  value={email.alert_email}
                  onChange={(e) => setEmail({ ...email, alert_email: e.target.value })}
                  placeholder="you@yourcompany.com"
                  className="w-full border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                />
              </Field>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Inbound support email &mdash; letting customers open tickets by emailing you &mdash;
                needs one DNS record and a port forward. Finish setup, then see{" "}
                <strong>HelpDesk &rarr; Setup</strong>.
              </p>
              <div className="flex justify-between">
                <button onClick={back} className="px-4 py-2 text-sm border rounded-lg dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/40">Back</button>
                <div className="flex gap-2">
                  <button onClick={next} className="px-4 py-2 text-sm border rounded-lg dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/40">Skip</button>
                  <button
                    onClick={saveEmail}
                    disabled={working}
                    className="px-4 py-2 text-sm bg-brand text-white rounded-lg disabled:opacity-50"
                  >
                    {working ? "Saving…" : "Save & next"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Activate your license. Paste the activation token from your provider (and the
                Hub URL + public key if supplied). You can skip and activate later from the
                License page.
              </p>
              <Field label="License Hub URL (optional)">
                <input
                  value={license.hub_url}
                  onChange={(e) => setLicense({ ...license, hub_url: e.target.value })}
                  placeholder="https://license.example.com"
                  className="w-full border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                />
              </Field>
              <Field label="Hub public key (base64, optional)">
                <input
                  value={license.public_key}
                  onChange={(e) => setLicense({ ...license, public_key: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 font-mono text-xs"
                />
              </Field>
              <Field label="Activation token">
                <textarea
                  value={license.token}
                  onChange={(e) => setLicense({ ...license, token: e.target.value })}
                  rows={3}
                  placeholder="v4.public...."
                  className="w-full border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 font-mono text-xs"
                />
              </Field>
              {licenseMsg && <p className="text-xs text-gray-600 dark:text-gray-300">{licenseMsg}</p>}
              <div className="flex justify-between">
                <button onClick={back} className="px-4 py-2 text-sm border rounded-lg dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/40">Back</button>
                <div className="flex gap-2">
                  <button onClick={next} className="px-4 py-2 text-sm border rounded-lg dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/40">Skip</button>
                  <button
                    onClick={saveLicense}
                    disabled={working}
                    className="px-4 py-2 text-sm bg-brand text-white rounded-lg disabled:opacity-50"
                  >
                    {working ? "Activating…" : "Activate & next"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">Review and finish.</p>
              <dl className="text-sm bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-1">
                <Row k="Admin" v={admin.username} />
                <Row k="Company" v={company.company_name} />
                <Row k="Timezone" v={company.app_timezone} />
                <Row k="Alerts to" v={email.alert_email || "—"} />
              </dl>
              <p className="text-xs text-gray-400">
                After finishing you'll be taken to the login screen. Sign in with the admin
                account you created.
              </p>
              <div className="flex justify-between">
                <button onClick={back} className="px-4 py-2 text-sm border rounded-lg dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/40">Back</button>
                <button
                  onClick={finish}
                  disabled={working}
                  className="px-4 py-2 text-sm bg-brand text-white rounded-lg disabled:opacity-50"
                >
                  {working ? "Finishing…" : "Finish setup"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{label}</span>
      {children}
    </label>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-gray-500 dark:text-gray-400">{k}</dt>
      <dd className="text-gray-900 dark:text-gray-100 font-medium">{v}</dd>
    </div>
  );
}

function StepNav({
  onBack,
  onNext,
  nextLabel,
  working,
  nextDisabled,
}: {
  onBack: () => void;
  onNext: () => void;
  nextLabel: string;
  working: boolean;
  nextDisabled?: boolean;
}) {
  return (
    <div className="flex justify-between pt-1">
      <button onClick={onBack} className="px-4 py-2 text-sm border rounded-lg dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/40">Back</button>
      <button
        onClick={onNext}
        disabled={working || nextDisabled}
        className="px-4 py-2 text-sm bg-brand text-white rounded-lg disabled:opacity-50"
      >
        {working ? "Working…" : nextLabel}
      </button>
    </div>
  );
}
